
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {AlertService} from "./alert.service";

/** Interfaces used */
import {Rule, Package, CDSNode, Org, OrgSettings} from './capd.model';
import { OauthService } from './oauth.service';
import { APIURL } from './url.config';
import {delay, retryWhen, scan} from 'rxjs/operators';
import { throwError } from "rxjs";
import {CDSTreeService} from "./cds.service";

/** Service for grabbing and modifying organizations and their rules. */
@Injectable()
export class OrgService {
  tenant: string = "";

  constructor(private http: HttpClient,
              private auth: OauthService,
              private alertService: AlertService) {
  }

  /**
   * Gets the settings configuration of a given organization.
   *
   * @async
   * @func getOrgSettings
   * @param {string} root - the CDS node ID whose settings you are retrieving.
   * @returns {Promise<Org>} an Org object for the given ID.
   */
  getOrgSettings(root: string) {
    if (root == null || root == 'null') {
      root = '~';
    }

    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.get<Org>(APIURL + 'api/orgs/' + root + '/settings/', options)
        .toPromise()
        .catch(err => {
          const org = {} as Org;
          const settings = {} as OrgSettings;
          org.settings = settings;
          org._id = root;
          return org;
        });
    })
    .catch(OrgService.handleError);
  }
  /**
   * Gets the settings configuration of a given organization.
   *
   * @async
   * @func getOrgSettings
   * @param {string} root - the CDS node ID whose settings you are retrieving.
   * @returns {Promise<Org>} an Org object for the given ID.
   */
  getOrgSettingsV2(root: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.get<Org>(APIURL + 'api/orgs/v2/' + root + '/settings/', options)
          .toPromise()
          .catch(err => {
            const org = {} as Org;
            const settings = {} as OrgSettings;
            org.settings = settings;
            org._id = root;
            return org;
          });
    })
        .catch(OrgService.handleError);
  }

  /**
   * Gets the settings configuration of a given organization.
   *
   * @async
   * @func getOrgSettings
   * @param {string} root - the CDS node ID whose settings you are retrieving.
   * @returns {Promise<Org>} an Org object for the given ID.
   */
  updateOrgSettingsV2(newSettings, root) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };
      const body = JSON.stringify(newSettings);

      return this.http.post(APIURL + 'api/orgs/v2/' + root + '/settings/', body, options)
          .toPromise().then(()=>{this.alertService.showAlert('Saved Successfully')})
          .catch(err => {
            const org = {} as Org;
            const settings = {} as OrgSettings;
            org.settings = settings;
            org._id = root;
            return org;
          });
    })
        .catch(OrgService.handleError);
  }

  /**
   * Gets the settings that match the type and string value.
   *
   * @async
   * @func searchAllSettings
   * @param {string} searchAllSettingsSearchType - the type of setting being searched for.
   * @param {string} searchAllSettingsSearchText - the value of setting being searched for.
   * @returns {Promise<Org>} an Org object for the type and string.
   */
  searchAllSettings(searchAllSettingsSearchType: string, searchAllSettingsSearchText: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.get<Org[]>(APIURL + 'api/orgs/searchsettings/?settingType=' + searchAllSettingsSearchType + '&searchText=' + searchAllSettingsSearchText , options)
          .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
            if (errorCount >= 3) {
              throw err;
            }
            return errorCount + 1;
          }, 0))))
          .pipe(delay(1000))
          .toPromise()
          .catch(err => throwError(err.json().error || 'Server error'));
    })
        .catch(OrgService.handleError);
  }

  searchAllSettingsV2(searchAllSettingsSearchType: string, searchAllSettingsSearchText: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.get<Org[]>(APIURL + 'api/orgs/v2/settings/search?settingName=' + searchAllSettingsSearchText , options)
          .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
            if (errorCount >= 3) {
              throw err;
            }
            return errorCount + 1;
          }, 0))))
          .pipe(delay(1000))
          .toPromise()
          .catch(err => throwError(err.json().error || 'Server error'));
    })
        .catch(OrgService.handleError);
  }

  /**
   * Gets the CDS Node of a given associated root ID.
   *
   * @async
   * @func getOrg
   * @param {string} root - the root ID of the CDS node.
   * @returns {Promise<CDSNode>} the CDS Node requested.
   */
  getOrg(root: string) {
    if (root == null || root == 'null') {
      root = '~';
    }

    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<CDSNode[]>(APIURL + 'api/cdstree/orgs/' + root + '/children?depth=0', options)
        .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
          if (errorCount >= 3) {
            throw err;
          }
          return errorCount + 1;
          // NOTE: Removed delay(5000) within the pipe below to prevent load delays on Org selection.
        }, 0))))
          .pipe(delay(1000))
        .toPromise()
        .then(res => res.filter(item => item.type !== 'SettingsGroup' && item.rootId === root)[0])
        .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  getOrgInfo(root: string) {
    if (root == null || root == 'null') {
      root = '~';
    }

    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<CDSNode>(APIURL + 'api/cdstree/orgs/' + root + "/", options)
          .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
            if (errorCount >= 3) {
              throw err;
            }
            return errorCount + 1;
            // NOTE: Removed delay(5000) within the pipe below to prevent load delays on Org selection.
          }, 0))))
          .pipe(delay(1000))
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  /**
   * Gets the settings groups of the given node.
   *
   * @async
   * @func getGroups
   * @param {CDSNode} root - the root node whose children you want.
   * @return {Promise<CDSNode[]>} a list of children as CDS Nodes.
   */
  getGroups(root: CDSNode) {
    if (root == null || root.rootId == null) {
      root.rootId = '~';
    }

    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/cdstree/build/orgs/' +
          root.rootId + '/groups/';
      const options = {headers: new HttpHeaders({Authorization: authorization})};

      return this.http.get<CDSNode[]>(url, options)
          .toPromise()
          .then(res => {
            return res;
          })
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  /**
   * Gets the settings groups of the given node.
   *
   * @async
   * @func getGroups
   * @param {CDSNode} root - the root node whose children you want.
   * @return {Promise<CDSNode[]>} a list of children as CDS Nodes.
   */
  getGroupsById(root: string) {
    if (root == null) {
      root = '~';
    }

    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/cdstree/build/orgs/' +
          root + '/groups/';
      const options = {headers: new HttpHeaders({Authorization: authorization})};

      return this.http.get<CDSNode[]>(url, options)
          .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
            if (errorCount >= 3) {
              throw err;
            }
            return errorCount + 1;
            // NOTE: Removed delay(5000) within the pipe below to prevent load delays on Org selection.
          }, 0)))).pipe(delay(1000))
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  /**
   * Gets a particular group of a given node.
   *
   * @async
   * @func getGroup
   * @param {string} root - the root node ID of the parent.
   * @param {string} group - the group you are searching for.
   * @returns {Promise<CDSNode>} a list of children as CDS Nodes.
   */
  getGroup(root: string, group: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/cdstree/orgs/' + root + '/children?depth=0';
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<CDSNode[]>(url, options)
        .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
          if (errorCount >= 3) {
            throw err;
          }
          return errorCount + 1;
          // NOTE: Removed delay(5000) within the pipe below to prevent load delays on Org selection.
        }, 0)))).pipe(delay(1000))
        .toPromise()
        .then(res => res.filter(item => item.extensionId === group)[0])
        .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  /**
   * Gets packages assigned to a given node.
   *
   * @async
   * @func getOrgPackages
   * @param {CDSNode} org - the node whose packages you are retrieving.
   * @returns {Promise<Package[]>} a list of packages that have been assigned.
   */
  getOrgPackages(org: CDSNode) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.get<Package[]>(APIURL + 'api/orgs/' + org.rootId + '/contentpackages', options)
        .toPromise()
        .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  getOrgPackagesAndRules(org: CDSNode) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.get<Package[]>(APIURL + 'api/orgs/' + org.rootId + '/contentpackages/details', options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  getRulesForGroups(root, groups: CDSNode[]) {
    return this.auth.getToken().then(token => {

      const groupId: string[] = [];
      groups.forEach(group => {
        groupId.push(group.rootId + '/' + group.extensionId);
      });

      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/contentpackages/orgs/' + root + '/groupOids/rules';
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.post(url, groupId, options)
          .toPromise()
          .catch(OrgService.handleError);

    });

  }

  getRulesForGroupsV2(groups: CDSNode[]) {
    return this.auth.getToken().then(token => {

      const groupId: string[] = [];
      groups.forEach(group => {
        groupId.push(group.rootId + '/' + group.extensionId);
      });

      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/groups/v2/content';
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
          'groupOids': groupId
        }) };

      return this.http.get(url, options)
          .toPromise()
          .catch(OrgService.handleError);

    });

  }

  /**
   * Opposite of above-- gets packages that have not been assigned to a given node.
   *
   * @async
   * @func getUnassignedOrgPackages
   * @param {string} org - the root ID of the org whose packages you are retrieving.
   * @returns {Promise<Package[]>} a list of packages that have not been assigned.
   */
  getUnassignedOrgPackages(org: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.get<Package[]>(APIURL + 'api/orgs/' + org + '/contentpackages?state=unassigned', options)
        .toPromise()
        .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  /**
   * Updates the settings of a given node.
   *
   * @async
   * @func updateOrg
   * @param {any} newSettings - an object containing the new settings.
   * @param {string} root - the organization ID.
   */
  updateOrg(newSettings, root: string) {
    return this.auth.getToken().then(token => {
      const body = JSON.stringify(newSettings);
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.post(APIURL + 'api/orgs/' + root + '/settings/', body, options)
        .toPromise().then(()=>{this.alertService.showAlert('Saved Successfully')})
        .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  /**
   * Pulls a package and assigns it to the organization.
   *
   * @async
   * @func pullPackageToOrg
   * @param {Package} pack - the package you wish to assign.
   * @param {string} root - the organization ID.
   */
  pullPackageToOrg(pack, root: string) {
    return this.auth.getToken().then(token => {
      const body = '';
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/contentpackages/' + pack.contentPackage_id + '/orgs/' + root + '/';
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.post(url, body, options)
        .toPromise()
        .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  /**
   * Removes references to a package from a given organization.
   *
   * @async
   * @func removePackageFromOrg
   * @param {string} pack - the name of the package you wish to remove.
   * @param {string} root - the OID of the group you wish to remove the package from.
   */
  removePackageFromOrg(pack: string, root: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;

      const url = APIURL + 'api/contentpackages/' + pack + '/orgs/' + root + '/delete';
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.delete(url, options)
        .toPromise()
        .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  /**
   * Given a list of rules, a list of groups, and a root node
   * assigns the list of rules to the list of groups.
   *
   * @async
   * @func assignRulesToGroup
   * @param {Rule[]} rules - the list of rules you want to assign.
   * @param {CDSNode[]} groups - the list of groups you want to assign rules too.
   * @param {string} root - the root ID of the parent organization.
   */
  assignRulesToGroup(rules: Rule[], groups: CDSNode[], root) {
    return this.auth.getToken().then(token => {
      const groupId: string[] = [];
      groups.forEach(group => {
        groupId.push(group.rootId + '/' + group.extensionId);
      });

      const strRules = JSON.stringify(rules);
      const strGroups = JSON.stringify(groupId);
      const body = '{ "contentPackageRules": ' + strRules + ', "groupOids": ' + strGroups + '}';
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/contentpackages/orgs/' + root + '/assignRulesToGroups';
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.post(url, body, options)
        .toPromise()
        .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  /**
   * Deletes rules assigned to a group.
   *
   * @async
   * @func deleteRulesForGroup
   * @param {rule[]} rules - the list of rules you wish to delete.
   * @param {string} group - the group you wish to remove rules from.
   * @param {string} root - the root ID of the org the grup belongs to.
   */
  deleteRulesForGroup(rules: Rule[], group, root) {
    return this.auth.getToken().then(token => {
      const body = JSON.stringify(rules);
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/contentpackages/orgs/' + root + '/groupOids/' + root + '%2B' + group + '/deleteRules';
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.put(url, body, options)
        .toPromise()
        .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  /**
   * Changes the state of a given subset of rules for a group.
   *
   * @async
   * @func updateRulesForGroup
   * @param {Rule[]} rules - the subset of rules being updated.
   * @param {string} group - the group ID.
   * @param {string} root - the ID of the parent organization.
   */
  updateRulesForGroup(rules: Rule[], group, root) {
    return this.auth.getToken().then(token => {
      const body = JSON.stringify(rules);
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/contentpackages/orgs/' + root + '/groupOids/' + root + '%2B' + group + '/rules';
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.put(url, body, options)
        .toPromise()
        .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  /** Handles errors by suppressing them.  Can be changed if needed for debug. */
  private static handleError(error: Response) {
    console.error(error);
    return throwError(error || 'Server error');
  }


    getOrgGroupRules() {
        return this.auth.getToken().then(token => {
            const authorization = 'bearer ' + token;
            const options = { headers: new HttpHeaders({
                    Authorization : authorization
                }) };

            return this.http.get<Rule[]>(APIURL + 'api/orgs/all_custom_rules', options)
                .toPromise()
                .catch(OrgService.handleError);
        })
            .catch(OrgService.handleError);
    }
  getOrgGroupRulesWithParams(enabledParam, oidParam) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          Authorization : authorization
        }) };
      if(enabledParam != "null")
        return this.http.get<Rule[]>(APIURL + 'api/orgs/all_custom_rules?orgOid=' + oidParam + '&enabled=' + enabledParam, options)
            .toPromise()
            .catch(OrgService.handleError);
      else
        return this.http.get<Rule[]>(APIURL + 'api/orgs/all_custom_rules?orgOid=' + oidParam, options)
            .toPromise()
            .catch(OrgService.handleError);
    })
    .catch(OrgService.handleError);
  }

  getAllOrgs(rootId: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<CDSNode[]>(APIURL + 'api/cdstree/orgs/oidToName/' + rootId + '/', options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  copyRulesToGroup(rules: Rule[], group, root, groups: CDSNode[]) {
    return this.auth.getToken().then(token => {

      const groupId: string[] = [];
      groups.forEach(group => {
        groupId.push(group.rootId + '/' + group.extensionId);
      });

      const strRules = JSON.stringify(rules)
      const strGroups = JSON.stringify(groupId)
      const body = '{ "contentPackageRules": ' + strRules + ', "groupOids": ' + strGroups + '}';
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/contentpackages/orgs/' + root + '/groupOids/' + root + '%2B' + group + '/copyRules';
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization
        }) };

      return this.http.put(url, body, options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  getOrgContent(orgId) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.get<Rule[]>(APIURL + 'api/orgs/v2/' + orgId + '/content', options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  getOrgGroupContent(orgId) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.get<Rule[]>(APIURL + 'api/orgs/v2/' + orgId + '/groupAssignments', options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  getUnassignedOrgContent(orgId) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.get<Rule[]>(APIURL + 'api/orgs/v2/' + orgId + '/unassigned', options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  updateOrgRule(orgId, triggerCode, rule) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };
      const body = rule;

      return this.http.put(APIURL + 'api/orgs/v2/' + orgId + '/rules/' + triggerCode, body, options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  deleteOrgRules(orgId, triggerCodes) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const body = triggerCodes;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }),
        body: body};

      return this.http.delete(APIURL + 'api/orgs/v2/' + orgId + '/rules', options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  assignRulesToGroupsV2(rules: Rule[], groups: CDSNode[], root) {
    return this.auth.getToken().then(token => {
      const groupId: string[] = [];
      groups.forEach(group => {
        groupId.push(group.rootId + '/' + group.extensionId);
      });

      let triggerCodes: string[] = [];

      rules.forEach(rule => {
        triggerCodes.push(rule.triggerCode);
      });

      const strTriggerCodes = JSON.stringify(triggerCodes);
      const strGroups = JSON.stringify(groupId);
      const body = '{ "triggerCodes": ' + strTriggerCodes + ', "groupOids": ' + strGroups + '}';
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/groups/v2/' + root + '/assign';
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.post(url, body, options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  /**
   * Contains the logic for filtering rules based on tenant
   *
   * @func shouldRuleDisplayBasedOnTenant
   */
  shouldRuleDisplayBasedOnTenant(rule: any, tenant: String): boolean {
    let result = false;
    if(!rule.customerTenants) {
      result = true;
    } else {
      let customerTenants: String[] = rule.customerTenants.split(',');
      customerTenants.forEach(customerTenant => {
        if(customerTenant && customerTenant.trim() === tenant) {
          result = true;
        }
      });
    }
    return result;
  }

  /**
   * Deletes rules assigned to a group.
   *
   * @async
   * @func deleteRulesForGroup
   * @param {rule[]} rules - the list of rules you wish to delete.
   * @param {string} group - the group you wish to remove rules from.
   * @param {string} root - the root ID of the org the grup belongs to.
   */
  deleteRulesForGroupV2(rules: Rule[], group, root) {
    return this.auth.getToken().then(token => {
      const triggerCodes: string[] = [];

      rules.forEach(rule => {
        triggerCodes.push(rule.triggerCode);
      });

      const body = JSON.stringify(triggerCodes);
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/groups/v2/' + root + '/' + group;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }),
        body: body};

      return this.http.delete(url, options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }


  /**
   * Updates rules assigned to a group.
   *
   * @async
   * @func deleteRulesForGroup
   * @param {rule[]} rules - the list of rules you wish to delete.
   * @param {string} group - the group you wish to remove rules from.
   * @param {string} root - the root ID of the org the grup belongs to.
   * @param {boolean} triggerSilent - boolean indiciating rule state
   */
  updateRulesForGroupV2(rules: Rule[], triggerSilent, group, root) {
    return this.auth.getToken().then(token => {
      const triggerCodes: string[] = [];

      rules.forEach(rule => {
        triggerCodes.push(rule.triggerCode);
      });

      const body = JSON.stringify(triggerCodes);
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/groups/v2/' + root + '/' + group;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
          triggerSilent : triggerSilent
        })};

      return this.http.put(url, body, options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }

  addOrgRules(orgId, rules) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const triggerCodes: string[] = [];

      rules.forEach(rule => {
        triggerCodes.push(rule.triggerCode);
      });

      const body = JSON.stringify(triggerCodes);
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        })};

      return this.http.post(APIURL + 'api/orgs/v2/' + orgId + '/content', body, options)
          .toPromise()
          .catch(OrgService.handleError);
    })
        .catch(OrgService.handleError);
  }
}
